import React, { FC, useState, useEffect, useContext } from 'react';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';

import BackflowDeviceForm from './BackflowDeviceForm';
import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';

import { backflowJobItemRequiredFields } from '../../backflowJobLists/constants/requiredFields';
import validateRequiredFields from '../../../utils/validateRequiredFields';

import { BackflowJobItem } from '../../model';

import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';

import axios from '../../../sharedModules/utils/axios.utils';

interface BackflowDeviceEditProps {
  backflowJobItem: BackflowJobItem;
  onSave: () => void;
  onCancel: () => void;
}

const BackflowDeviceEdit: FC<BackflowDeviceEditProps> = ({ backflowJobItem, onSave, onCancel }) => {
  const { setSnackBarOpen, setSnackBarMessage, handleSnackbarAlertSeverityChange } = useContext(SnackBarContext);

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [formValues, setFormValues] = useState<BackflowJobItem>(backflowJobItem);
  const [loading, setLoading] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<string>('');
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);

  useEffect(() => {
    if (backflowJobItem?.id) {
      setFormValues(backflowJobItem);
    }
  }, [backflowJobItem]);

  // Monitor online/offline status
  useEffect(() => {
    const updateOnlineStatus = () => {
      setIsOnline(navigator.onLine);
    };

    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);

    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
    };
  }, []);

  // Update cached job item in localStorage
  const updateCachedJobItem = (markAsNeedsSync: boolean = true): boolean => {
    try {
      const jobGroupId = backflowJobItem?.backflow_job_group_id;
      console.log('jobGroupId', jobGroupId);
      if (!jobGroupId) return false;

      const cacheKey = `backflowJobGroup_${jobGroupId}`;
      const cachedData = localStorage.getItem(cacheKey);
      console.log('cachedData', cachedData);
      if (!cachedData) {
        setErrorMessage('Job group not cached. Please visit the job group page while online first.');
        return false;
      }

      const jobGroupData = JSON.parse(cachedData);
      console.log('jobGroupData', jobGroupData);
      // Find and update the job item in the cached job items array
      const itemIndex = jobGroupData.jobItems?.findIndex((item: any) => item.id === backflowJobItem.id);
      console.log('itemIndex', itemIndex);
      if (itemIndex >= 0) {
        // Update backflow_job_item sub-object
        jobGroupData.jobItems[itemIndex].backflow_job_item = {
          asset_no: formValues.asset_no,
          backflow_type: formValues.backflow_type,
          bfp_manufacturer_id: formValues.bfp_manufacturer_id,
          location: formValues.location,
          model_no: formValues.model_no,
          serial_no: formValues.serial_no,
          status: formValues.status,
          water_meter_serial: formValues.water_meter_serial,
          water_meter_serial_secondary: formValues.water_meter_serial_secondary,
          wm_manufacturer_id: formValues.wm_manufacturer_id,
          needsSync: markAsNeedsSync,
          savedAt: new Date().toISOString(),
        };
        // backflow_log remains unchanged
      } else {
        setErrorMessage('Job item not found in cache.');
        return false;
      }

      // Save back to localStorage
      localStorage.setItem(cacheKey, JSON.stringify(jobGroupData));

      return true;
    } catch (error) {
      setErrorMessage('Failed to update cached data.');
      return false;
    }
  };

  const handleUpdate = async () => {
    const validate = validateRequiredFields(formValues, backflowJobItemRequiredFields);
    setValidationErrors(validate.message);
    if (!validate.isValid) return;

    const assetNo = formValues?.asset_no || '';
    if (assetNo.length > 20 || /\s/.test(assetNo)) {
      setValidationErrors('Asset # must be a single word with no spaces and 20 characters or fewer');
      return;
    }

    setLoading(true);

    // If offline, update cache only
    if (!isOnline) {
      const success = updateCachedJobItem(true); // Mark as needs sync

      if (success) {
        setSnackBarMessage('Updated device info on your device. Changes will sync when you have connectivity.');
        handleSnackbarAlertSeverityChange('info');
        setSnackBarOpen(true);
        setErrorMessage('');
        onSave();
      }

      setLoading(false);
      return;
    }

    // If online, save to server
    try {
      await axios.put(`/backflows/backflow_job_items/${backflowJobItem.id}`, {
        backflow_job_item: formValues,
      });

      // Also update cache with fresh data (mark as synced)
      updateCachedJobItem(false);

      setSnackBarMessage('Updated Backflow Device Info');
      handleSnackbarAlertSeverityChange('success');
      setSnackBarOpen(true);
      setErrorMessage('');
      onSave();
    } catch (error) {
      setErrorMessage('Error Updating Backflow Device Info');
      setSnackBarMessage('Error Updating Backflow Device Info');
      handleSnackbarAlertSeverityChange('error');
      setSnackBarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoadingWrapper isLoading={loading}>
      <Card>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <BackflowDeviceForm formValues={formValues} setFormValues={setFormValues} />
            </Grid>
            <Grid item xs={12}>
              <ErrorMessage message={errorMessage || validationErrors} />
            </Grid>
            {!isOnline && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  You are currently offline. Changes will be saved to your device and can be submitted when you are
                  connected.
                </Alert>
              </Grid>
            )}
            <Grid item>
              <Button variant="contained" onClick={handleUpdate}>
                {isOnline ? 'Update' : 'Save to Device'}
              </Button>
            </Grid>
            <Grid item>
              <Button variant="contained" color="secondary" onClick={onCancel}>
                Cancel
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </LoadingWrapper>
  );
};

export default BackflowDeviceEdit;
